// src/pages/Itineraries/UnifiedItineraryView.jsx
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  Suspense,
} from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "../../../../utils/toast";
import { useUser } from "../../../../context/UserContext";
import { getLandingPathForRole } from "../../../../utils/roleUtils";
import useItineraryData from "../../../../hooks/useItineraryData";
import { emitItineraryUpdate, emitTripRename } from "../../../../utils/pusherEvents";
import {
  ArrowLeft,
  Download,
  RefreshCw,
  Share2,
  Edit3,
  Calendar,
  MapPin,
  Clock,
  Sun,
  Moon,
  Utensils,
  Layers,
  Plane,
  PlaneTakeoff,
  Bed,
  ShoppingBag,
  Star,
  ChevronDown,
  X,
  Ticket,
  Camera,
  Music,
  Coffee,
  Filter,
  MoreVertical,
  Save,
  Trash2,
  Copy,
  Eye,
  EyeOff,
  FileText,
} from "lucide-react";
import ItineraryErrorBoundary from "../../../../components/agent/UX/ItineraryErrorBoundary";
import RestaurantSelectionIndicator from "../../../../components/agent/RestaurantSelectionIndicator";
import DragDropActivity from "../../../../components/agent/ItineraryEditor/DragDropActivity";
import ActivityTiming from "../../../../components/agent/ActivityTiming";
// Active layout component (default)
import ItineraryDaysAccordion from "./ItineraryDaysAccordion";

// Day cards layout - V2 (segments_v2) primary when present, legacy fallback
const ItineraryDaysV2 = React.lazy(() => import("./ItineraryDaysV2"));
const ItineraryDaysLegacy = React.lazy(() => import("./ItineraryDaysLegacy"));
import {
  normalizeItineraryData,
  extractGuestInfo,
  analyzeDayType,
  safeJsonParse,
  processAgentNotes,
  getDiningInfo,
  getActivitiesByTimeBlock,
  getAllRides,
  getAllShows,
  getAllRetail,
  hasDiningInfo,
  getSpecialtyExperiences,
  getAllSpecialtyExperiences,
  hasSpecialtyExperiences,
  sortActivitiesByTime,
  ensureMonotonicSchedule,
  getDisplayZone,
  transformItineraryData,
} from "../../../../utils/itineraryDataProcessor";
import ClientSnapshot from "./ClientSnapshot";
import { decodeHtmlEntities } from "../../../../utils/textUtils";
import { parseDateForCompare } from "../../../../utils/dateUtils";

/** Map unmet dining reasonCode to friendly copy (Phase 2 / ITINERARY_RENDERING_RULES). */
function unmetDiningReasonLabel(entry) {
  const code = entry?.reasonCode || entry?.reason || "";
  const map = {
    not_found_in_dataset: "Not found in our restaurant list",
    excluded_by_filters: "Excluded by your filters",
    not_in_selected_parks: "Not in your selected parks",
    time_conflict: "Could not fit in your schedule",
    time_window_conflict: "Could not fit in your time window",
    park_not_in_day: "Not in this day's parks",
    capacity_overflow: "Too many must-dos for the day",
    ambiguous_match: "Multiple matches; please specify",
    outside_block_window: "Outside this park's block window (e.g. MK morning-only day)",
  };
  if (code === "outside_block_window" && entry?.detail) {
    const detailMap = {
      "start-before-block": "Starts before block window",
      "end-after-block": "Ends after block window (duration spill)",
      "start-only-check": "Could not verify full reservation within block (start-only check)",
      "assumed-60-min": "Assumed 60 min reservation would extend past block end",
    };
    const detailLabel = detailMap[entry.detail] || entry.detail;
    return `${map[code] || "Outside block window"} — ${detailLabel}`;
  }
  return map[code] || (typeof entry?.reason === "string" ? entry.reason : "Could not be scheduled");
}

/** Renders one activity row based on type. Used for chronological display (prevents time-travel). */
function ActivityRow({ activity, blockPrefix, index }) {
  const t = activity?.type || "note";
  const isRide = t === "ride" || t === "attraction" || t === "unique_experience" || t === "family_ride";
  const isShow = t === "show" || t === "live_show";
  const isTransfer = t === "transfer" || t === "park_transition" || (t === "note" && String(activity?.name || "").startsWith("Transfer"));
  const key = `${blockPrefix}-${index}`;
  const zoneOrLand = activity?.displayLand ?? activity?.displayZone ?? activity?.zone ?? activity?.location ?? activity?.meta?.primaryLand ?? null;
  if (isTransfer) {
    const toPark = activity?.meta?.toPark || (typeof activity?.name === "string" && activity.name.replace(/^Transfer to\s+/i, "")) || "";
    const label = toPark ? `Transfer to ${toPark}` : (typeof activity === "string" ? activity : (activity?.name || "Transfer"));
    return (
      <div key={key} className="flex gap-2 items-center px-3 py-2 bg-white rounded-lg border border-gray-200">
        <Clock className="w-3 h-3 text-gray-600" />
        <div className="flex-1"><span className="text-xs font-medium text-gray-900">{label}</span><ActivityTiming activity={activity} /></div>
        <span className="px-1.5 py-0.5 text-xs text-gray-700 bg-gray-100 rounded-full">Transfer</span>
      </div>
    );
  }
  if (isRide) {
    return (
      <div key={key} className="flex gap-2 items-center px-3 py-2 bg-white rounded-lg border border-blue-200">
        <Ticket className="w-3 h-3 text-blue-600" />
        <div className="flex-1">
          <span className="text-xs font-medium text-gray-900">{activity.name || activity}</span>
          {zoneOrLand && <span className="ml-1 text-xs text-gray-500">({zoneOrLand})</span>}
          <ActivityTiming activity={activity} />
        </div>
        <span className="px-1.5 py-0.5 text-xs text-blue-800 bg-blue-100 rounded-full">Ride</span>
      </div>
    );
  }
  if (isShow) {
    return (
      <div key={key} className="flex gap-2 items-center px-3 py-2 bg-white rounded-lg border border-purple-200">
        <Music className="w-3 h-3 text-purple-600" />
        <div className="flex-1">
          <span className="text-xs font-medium text-gray-900">{activity.name || activity}</span>
          {zoneOrLand && <span className="ml-1 text-xs text-gray-500">({zoneOrLand})</span>}
          <ActivityTiming activity={activity} />
        </div>
        <span className="px-1.5 py-0.5 text-xs text-purple-800 bg-purple-100 rounded-full">Show</span>
      </div>
    );
  }
  if (t === "buffer" || t === "coverage_filler") {
    return (
      <div key={key} className="flex gap-2 items-center px-3 py-2 bg-white rounded-lg border border-gray-200">
        <Clock className="w-3 h-3 text-gray-600" />
        <div className="flex-1"><span className="text-xs font-medium text-gray-700">{activity.name || (t === "buffer" ? "Flexible time" : "Rest")}</span><ActivityTiming activity={activity} /></div>
        <span className="px-1.5 py-0.5 text-xs text-gray-600 bg-gray-100 rounded-full">Flexible</span>
      </div>
    );
  }
  if (t === "celebration_experience") {
    return (
      <div key={key} className="flex gap-2 items-center px-3 py-2 bg-white rounded-lg border border-amber-200">
        <Star className="w-3 h-3 text-amber-600" />
        <div className="flex-1"><span className="text-xs font-medium text-gray-900">{activity.name || activity}</span><ActivityTiming activity={activity} /></div>
        <span className="px-1.5 py-0.5 text-xs text-amber-800 bg-amber-100 rounded-full">Celebration</span>
      </div>
    );
  }
  if (t === "character_meet") {
    return (
      <div key={key} className="flex gap-2 items-center px-3 py-2 bg-white rounded-lg border border-teal-200">
        <Camera className="w-3 h-3 text-teal-600" />
        <div className="flex-1 min-w-0"><span className="text-xs font-medium text-gray-900">{activity.characterName || activity.name || activity}</span>{activity.location && <p className="text-xs text-gray-600 mt-0.5 truncate">{activity.location}</p>}<ActivityTiming activity={activity} /></div>
        <span className="px-1.5 py-0.5 text-xs text-teal-800 bg-teal-100 rounded-full shrink-0">{activity.choiceSource === "client" || activity.__mustMeetCharacter ? "Client Choice" : "Character"}</span>
      </div>
    );
  }
  if (t === "retail") {
    return (
      <div key={key} className="flex gap-2 items-center px-3 py-2 bg-white rounded-lg border border-pink-200">
        <ShoppingBag className="w-3 h-3 text-pink-600" />
        <div className="flex-1"><span className="text-xs font-medium text-gray-900">{activity.name || activity}</span><ActivityTiming activity={activity} /></div>
        <span className="px-1.5 py-0.5 text-xs text-pink-800 bg-pink-100 rounded-full">Shopping</span>
      </div>
    );
  }
  if (t === "dining") {
    return (
      <div key={key} className="flex gap-2 items-center px-3 py-2 bg-white rounded-lg border border-green-200">
        <Utensils className="w-3 h-3 text-green-600" />
        <div className="flex-1">
          <span className="text-xs font-medium text-gray-900">{activity.name || activity}</span>
          {(activity.restaurant?.serviceType === "TS" || activity.serviceType === "TS") && <span className="ml-1 text-xs text-gray-500">Table Service</span>}
          {(activity.restaurant?.serviceType === "QS" || activity.serviceType === "QS") && <span className="ml-1 text-xs text-gray-500">Quick Service</span>}
          <ActivityTiming activity={activity} />
        </div>
        <RestaurantSelectionIndicator isClientSelected={activity.choiceSource === "client" || activity.__fromMustTry === true} showLabel={false} />
      </div>
    );
  }
  return (
    <div key={key} className="flex gap-2 items-center px-3 py-2 bg-white rounded-lg border border-gray-200">
      <Clock className="w-3 h-3 text-gray-600" />
      <div className="flex-1"><span className="text-xs font-medium text-gray-700">{activity.name || activity}</span><ActivityTiming activity={activity} /></div>
      <span className="px-1.5 py-0.5 text-xs text-gray-600 bg-gray-100 rounded-full">Note</span>
    </div>
  );
}

/**
 * UnifiedItineraryView — refreshed
 * - Sticky action bar
 * - Filter chips + jump-to-date
 * - Hopper title for 2+ parks
 * - Compact scannable day cards (first 2–3 concrete items)
 * - Accessible modal with focus-trap + ESC/overlay close
 * - Env-based client share URL
 * - Renaming bug fixed; trip details keys unified
 * - Basic print stylesheet
 */

const PRINT_STYLES = `
@media print {
  @page { 
    margin: 12mm; 
    size: A4;
  }
  
  body { 
    background: #fff !important; 
    color: #000 !important;
    font-size: 12pt;
    line-height: 1.4;
  }
  
  #appHeader, #filtersRow, #actionsRail, .no-print, .print-hidden { 
    display: none !important; 
  }
  
  .day-card { 
    break-inside: avoid; 
    page-break-inside: avoid;
    border: 1px solid #ddd !important;
    margin-bottom: 1rem;
    padding: 1rem;
    background: #fff !important;
  }
  
  .day-card h3 {
    color: #000 !important;
    font-size: 14pt;
    font-weight: bold;
    margin-bottom: 0.5rem;
  }
  
  .day-card p {
    color: #333 !important;
    margin-bottom: 0.5rem;
  }
  
  .activity-badges {
    margin: 0.5rem 0;
  }
  
  .activity-badges span {
    background: #f0f0f0 !important;
    color: #000 !important;
    border: 1px solid #ccc !important;
    padding: 0.2rem 0.5rem;
    margin: 0.2rem;
    border-radius: 0.3rem;
    font-size: 10pt;
  }
  
  .magic-message {
    background: #fff3cd !important;
    border: 1px solid #ffeaa7 !important;
    padding: 0.5rem;
    margin: 0.5rem 0;
    border-radius: 0.3rem;
  }
  
  .magic-message div {
    color: #856404 !important;
  }
  
  /* Print header */
  .print-header {
    display: block !important;
    text-align: center;
    margin-bottom: 2rem;
    padding-bottom: 1rem;
    border-bottom: 2px solid #000;
  }
  
  .print-header h1 {
    font-size: 18pt;
    font-weight: bold;
    margin-bottom: 0.5rem;
  }
  
  .print-header .trip-details {
    font-size: 11pt;
    color: #666;
  }
  
  /* Grid layout for print */
  .print-grid {
    display: grid !important;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }
  
  @media print and (max-width: 8.5in) {
    .print-grid {
      grid-template-columns: 1fr;
    }
  }
}
`;

// Utilities
const formatDate = (d) => {
  if (!d) return "TBD";

  // Handle date strings that should be treated as local dates, not UTC
  let dt;
  if (typeof d === 'string' && d.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = d.split('-').map(Number);
    dt = new Date(year, month - 1, day);
  } else {
    dt = new Date(d);
  }

  if (isNaN(dt.getTime())) return "TBD";
  return dt.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

/** True if day has a parsable date for comparison/display. */
const hasValidDayDate = (info) => {
  if (!info?.date) return false;
  return parseDateForCompare(info.date) !== null;
};

const formatHopperTitle = (parks) => {
  if (!parks || parks.length === 0) return "";
  if (parks.length <= 3) return parks.join(" & ");
  return `${parks.slice(0, 3).join(" • ")} +${parks.length - 3} more`;
};

const getDayParks = (info) => {
  // Handle null/undefined info
  if (!info) {
    return [];
  }

  // New structure with parks array
  if (info.parks && Array.isArray(info.parks)) {
    return info.parks;
  }
  
  // Fallback to legacy structure
  const t = analyzeDayType(info);
  if (t.hopperParks?.length) return t.hopperParks;
  const p = info.park || info.Park;
  return p ? [p] : [];
};

/** Friendly label for minMeaningfulShortfall reasonCode. */
function minMeaningfulShortfallLabel(entry) {
  const code = entry?.reasonCode || "";
  if (code === "insufficient_eligible_overflow") return "Not enough eligible activities in overflow to meet block target.";
  if (code === "no_anchor_attraction_in_block") return "No anchor attraction in block; filler not inserted.";
  if (code === "min_meaningful_dropped_after_prune") return "Minimum meaningful count not met after pruning filler outside block window.";
  return "Block did not meet minimum meaningful count.";
}

/** Day-level warnings: unmet dining (reasonCode), unmet characters, minMeaningfulShortfall, landCommitmentRelaxed. Phase 2. */
const DayWarningsPanel = ({ warnings, unresolvedDining }) => {
  const unmetDining = Array.isArray(warnings?.unmetDining) ? warnings.unmetDining : (Array.isArray(unresolvedDining) ? unresolvedDining.map((u) => (typeof u === "object" ? u : { name: String(u), reason: "unresolved" })) : []);
  const unmetCharacters = Array.isArray(warnings?.unmetCharacters) ? warnings.unmetCharacters : [];
  const minMeaningfulShortfall = Array.isArray(warnings?.minMeaningfulShortfall) ? warnings.minMeaningfulShortfall : [];
  const landCommitmentRelaxed = Array.isArray(warnings?.landCommitmentRelaxed) ? warnings.landCommitmentRelaxed : [];
  if (unmetDining.length === 0 && unmetCharacters.length === 0 && minMeaningfulShortfall.length === 0 && landCommitmentRelaxed.length === 0) return null;
  return (
    <div className="p-4 mt-4 rounded-xl border border-amber-200 itinerary-warnings-container bg-amber-50/80">
      <h4 className="mb-3 text-sm font-semibold text-gray-800">Notes</h4>
      <div className="space-y-2">
        {unmetDining.map((entry, i) => (
          <div key={`dining-${i}`} className="flex flex-wrap gap-2 items-center text-xs">
            <span className="font-medium text-gray-900">{entry?.name ?? "Dining"}</span>
            <span className="text-amber-800">{unmetDiningReasonLabel(entry)}</span>
          </div>
        ))}
        {unmetCharacters.map((entry, i) => (
          <div key={`char-${i}`} className="flex flex-wrap gap-2 items-center text-xs">
            <span className="font-medium text-gray-900">{entry?.character ?? entry?.name ?? "Character"}</span>
            <span className="text-amber-800">{entry?.reason ?? "Could not be scheduled"}</span>
          </div>
        ))}
        {minMeaningfulShortfall.map((entry, i) => (
          <div key={`shortfall-${i}`} className="flex flex-wrap gap-2 items-center text-xs">
            <span className="font-medium text-gray-900">{entry?.park ?? "Park"}</span>
            {entry?.blockType && (
              <span className="text-gray-600">({entry.blockType})</span>
            )}
            <span className="text-amber-800">{minMeaningfulShortfallLabel(entry)}</span>
            {(entry?.achieved != null || entry?.required != null) && (
              <span className="text-gray-600">({entry.achieved ?? "—"} / {entry.required ?? "—"} meaningful)</span>
            )}
          </div>
        ))}
        {landCommitmentRelaxed.map((entry, i) => (
          <div key={`land-relaxed-${i}`} className="flex flex-wrap gap-2 items-center text-xs">
            <span className="font-medium text-gray-900">{entry?.park ?? "DHS"}</span>
            {entry?.blockType && (
              <span className="text-gray-600">({entry.blockType})</span>
            )}
            <span className="text-amber-800">
              DHS land commitment expanded beyond {entry?.primaryLand ?? "primary land"} because {entry?.reasonCode === "insufficient_primary_land_candidates" ? "not enough activities in primary land" : entry?.reasonCode ?? "land expansion"}.
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

/** Arrival/departure/rest day meal suggestions (non-scheduled). Renders only if mealSuggestions is non-empty. */
const MealSuggestionsPanel = ({ mealSuggestions }) => {
  if (!Array.isArray(mealSuggestions) || mealSuggestions.length === 0) return null;
  const order = ["lunch", "dinner"];
  const byMeal = order.map((m) => mealSuggestions.filter((b) => (b?.mealType || "") === m)).filter((arr) => arr.length > 0);
  if (byMeal.length === 0) return null;
  const mealLabel = (m) => ({ lunch: "Lunch Suggestions", dinner: "Dinner Suggestions" }[m] || `${m} Suggestions`);
  const serviceLabel = (s) => {
    if (s?.service === "QS") return "Quick Service";
    if (s?.service === "TS") return "Table Service";
    if (s?.type) return typeof s.type === "string" ? s.type : "—";
    return null;
  };
  return (
    <div className="p-4 mt-4 rounded-xl border itinerary-suggestions-container border-slate-200 bg-slate-50/80">
      <h4 className="mb-3 text-sm font-semibold text-gray-800">Meal Suggestions</h4>
      <div className="space-y-4">
        {byMeal.flatMap((blocks) =>
          blocks.map((block, bidx) => (
            <div key={`${block?.mealType ?? ""}-${bidx}`} className="space-y-2">
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-sm font-medium text-gray-800">{mealLabel(block?.mealType)}</span>
                {block?.domain && (
                  <span className={`itinerary-badge-domain px-2 py-0.5 rounded text-xs font-medium ${block.domain === "disney_springs" ? "bg-blue-100 text-blue-800" : "bg-slate-200 text-slate-700"}`}>
                    {block.domain === "disney_springs" ? "Disney Springs" : "Resort"}
                  </span>
                )}
              </div>
              {block?.note && <p className="text-xs text-gray-600">{block.note}</p>}
              <div className="space-y-1.5">
                {Array.isArray(block?.suggestions)
                  ? block.suggestions.map((s, i) => (
                      <div key={i} className="flex flex-wrap gap-2 items-center px-3 py-2 text-sm bg-white rounded-lg border border-slate-200">
                        <span className="font-medium text-gray-900">{s?.name ?? "—"}</span>
                        {s?.category && (
                          <span className="itinerary-badge-category px-1.5 py-0.5 rounded text-xs bg-indigo-100 text-indigo-800">
                            {typeof s.category === "string" && s.category.length > 40 ? s.category.split(" – ")[0] : s.category}
                          </span>
                        )}
                        {serviceLabel(s) && (
                          <span className="itinerary-badge-service px-1.5 py-0.5 rounded text-xs bg-green-100 text-green-800">{serviceLabel(s)}</span>
                        )}
                        {s?.location && <span className="text-xs text-gray-500">{getDisplayZone(s?.location) ?? s.location}</span>}
                      </div>
                    ))
                  : null}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const hasDining = (info) => {
  return hasDiningInfo(info);
};

const hasNighttime = (info) => {
  if (!info) return false;
  if (info.nighttime) return true;
  const sections = info.sections || [];
  return sections.some((s) =>
    /night|fireworks|parade|fantasmic|after dark/i.test(
      s.title || s.content || ""
    )
  );
};

const _summarizeSection = (section) => {
  if (!section) return null;
  if (Array.isArray(section)) {
    const names = section
      .map((x) => x?.name || x?.title || (typeof x === "string" ? x : ""))
      .filter(Boolean);
    return names.slice(0, 3).join(" • ");
  }
  if (typeof section === "object") {
    const out = [];
    if (Array.isArray(section.rides))
      out.push(...section.rides.map((r) => r.name || (typeof r === 'string' ? r : 'Unknown Ride')).slice(0, 2));
    if (Array.isArray(section.activities))
      out.push(...section.activities.map((a) => a.name || (typeof a === 'string' ? a : 'Unknown Activity')).slice(0, 2));
    if (section.activity) out.push(typeof section.activity === 'string' ? section.activity : 'Unknown Activity');
    if (section.description) out.push(section.description);
    return out.filter(Boolean).slice(0, 3).join(" • ");
  }
  return String(section)
    .split(/[•,\n;-]+/g)
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, 3)
    .join(" • ");
};

// Get activity badges for a day based on new data structure
const _getActivityBadges = (day) => {
  console.log('🔍 getActivityBadges called with day:', day);
  const badges = [];
  
  // Count rides/attractions using new utility function
  const rides = getAllRides(day);
  console.log('🎢 Rides found:', rides);
  if (rides.length > 0) {
    badges.push({ 
      type: 'rides', 
      count: rides.length, 
      icon: Ticket, 
      color: 'blue' 
    });
  }
  
  // Count shows using new utility function
  const shows = getAllShows(day);
  console.log('🎭 Shows found:', shows);
  if (shows.length > 0) {
    badges.push({ 
      type: 'shows', 
      count: shows.length, 
      icon: Music, 
      color: 'purple' 
    });
  }
  
  // Count dining using new utility function
  const hasDining = hasDiningInfo(day);
  console.log('🍽️ Has dining info:', hasDining);
  if (hasDining) {
    badges.push({ 
      type: 'dining', 
      count: 2, // Always lunch and dinner
      icon: Utensils, 
      color: 'green' 
    });
  }
  
  // Count retail/shopping activities using new utility function
  const retail = getAllRetail(day);
  console.log('🛍️ Retail activities found:', retail);
  if (retail.length > 0) {
    badges.push({ 
      type: 'shopping', 
      count: retail.length, 
      icon: ShoppingBag, 
      color: 'pink' 
    });
  }
  
  // Count specialty experiences using new utility function
  const experiences = getSpecialtyExperiences(day);
  console.log('⭐ Experiences found:', experiences);
  if (experiences.length > 0) {
    badges.push({ 
      type: 'experiences', 
      count: experiences.length, 
      icon: Star, 
      color: 'yellow' 
    });
  }
  
  console.log('🏷️ Final badges:', badges);
  return badges;
};

// Get detailed activities for modal based on new data structure
const _getDetailedActivities = (day) => {
  const sections = [];
  
  // Add rides section if there are rides
  const rides = getAllRides(day);
  if (rides.length > 0) {
    sections.push({
      title: 'Rides & Attractions',
      activities: rides.map(ride => ({ 
        type: 'ride', 
        name: String(ride), 
        icon: Ticket 
      }))
    });
  }
  
  // Add shows section if there are shows
  const shows = getAllShows(day);
  if (shows.length > 0) {
    sections.push({
      title: 'Shows & Entertainment',
      activities: shows.map(show => ({ 
        type: 'show', 
        name: String(show), 
        icon: Music 
      }))
    });
  }
  
  // Add retail/shopping section if there are retail activities
  const retail = getAllRetail(day);
  if (retail.length > 0) {
    sections.push({
      title: 'Shopping & Retail',
      activities: retail.map(shop => ({ 
        type: 'retail', 
        name: String(shop), 
        icon: ShoppingBag 
      }))
    });
  }
  
  // Add dining section using new dining structure
  if (hasDiningInfo(day)) {
    try {
      const diningInfo = getDiningInfo(day);
      const diningActivities = [];
      
      if (diningInfo.lunch && typeof diningInfo.lunch === 'string' && diningInfo.lunch !== "Lunch not specified") {
        diningActivities.push({ 
          type: 'dining', 
          name: String(diningInfo.lunch), 
          icon: Utensils,
          mealType: 'Lunch',
          clientSelected: diningInfo.lunchClientSelected || false
        });
      }
      
      if (diningInfo.dinner && typeof diningInfo.dinner === 'string' && diningInfo.dinner !== "Dinner not specified") {
        diningActivities.push({ 
          type: 'dining', 
          name: String(diningInfo.dinner), 
          icon: Utensils,
          mealType: 'Dinner',
          clientSelected: diningInfo.dinnerClientSelected || false
        });
      }
      
      if (diningActivities.length > 0) {
        sections.push({
          title: 'Dining',
          activities: diningActivities
        });
      }
    } catch (error) {
      console.warn("Error processing dining info:", error);
    }
  }
  
  // Add specialty experiences section if there are experiences
  // Use getSpecialtyExperiences for day-specific, but could also use getAllSpecialtyExperiences
  const experiences = getSpecialtyExperiences(day);
  if (experiences.length > 0) {
    sections.push({
      title: 'Specialty Experiences',
      activities: experiences.map(experience => ({ 
        type: 'experience', 
        name: String(experience), 
        icon: Star 
      }))
    });
  }
  
  // Note: getAllSpecialtyExperiences is available for cross-day experience aggregation if needed
  
  // Add time-based sections using new timeBlocks structure
  const timeBlocks = getActivitiesByTimeBlock(day);
  
  if (timeBlocks.morning.length > 0) {
    sections.push({
      title: 'Morning',
      activities: timeBlocks.morning.map(activity => ({ 
        type: activity.type || 'activity', 
        name: typeof activity === 'string' ? activity : (activity.name || 'Unknown Activity'), 
        icon: Clock 
      }))
    });
  }
  
  if (timeBlocks.afternoon.length > 0) {
    sections.push({
      title: 'Afternoon',
      activities: timeBlocks.afternoon.map(activity => ({ 
        type: activity.type || 'activity', 
        name: typeof activity === 'string' ? activity : (activity.name || 'Unknown Activity'), 
        icon: Clock 
      }))
    });
  }
  
  if (timeBlocks.evening.length > 0) {
    sections.push({
      title: 'Evening',
      activities: timeBlocks.evening.map(activity => ({ 
        type: activity.type || 'activity', 
        name: typeof activity === 'string' ? activity : (activity.name || 'Unknown Activity'), 
        icon: Clock 
      }))
    });
  }
  
  return sections;
};

// NOTE: DayCard and DayQuickSummary components have been moved to:
// - ItineraryDaysOverview.jsx (legacy grid layout)
// - ItineraryDaysOverview.timeline.jsx (new timeline layout)

const UnifiedItineraryView = ({ styleVariant = 'unified' }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useUser();
  const [hasRedirected, setHasRedirected] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  
  // Layout enum for days overview
  const LAYOUTS = {
    LEGACY: "legacy",
    ACCORDION: "accordion",
  };

  // Layout switcher: URL query param > localStorage > default (accordion)
  // Usage: ?layout=legacy|accordion
  // The preference is also saved to localStorage for persistence
  const [layout, setLayout] = useState(() => {
    const layoutParam = searchParams.get('layout');
    
    // URL query param takes precedence
    if (layoutParam === LAYOUTS.ACCORDION || layoutParam === LAYOUTS.LEGACY) {
      localStorage.setItem('itineraryLayout', layoutParam);
      return layoutParam;
    }
    
    // Fall back to localStorage if no query param
    const savedLayout = localStorage.getItem('itineraryLayout');
    if (savedLayout === LAYOUTS.ACCORDION || savedLayout === LAYOUTS.LEGACY) {
      return savedLayout;
    }
    
    // Default to accordion layout
    return LAYOUTS.ACCORDION;
  });

  // Update layout when URL param changes
  useEffect(() => {
    const layoutParam = searchParams.get('layout');
    if (layoutParam === LAYOUTS.ACCORDION || layoutParam === LAYOUTS.LEGACY) {
      setLayout(layoutParam);
      localStorage.setItem('itineraryLayout', layoutParam);
    }
  }, [searchParams]);

  // Validate route param id
  useEffect(() => {
    if ((!id || typeof id !== 'string' || id.trim().length === 0) && !hasRedirected) {
      console.error('[ItineraryView] Invalid or missing route param id - redirecting to trips');
      setHasRedirected(true);
      navigate('/agent/crm/trips', { replace: true });
    }
  }, [id, navigate, hasRedirected]);

  // Mount transition - trigger after first render
  useEffect(() => {
    // Use requestAnimationFrame to ensure transition happens after initial render
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setIsMounted(true);
      });
    });
  }, []);

  // Use the shared hook for data fetching
  const {
    itinerary,
    days,
    segmentsV2,
    tripName,
    setTripName,
    itineraryId,
    tripDetails,
    magicMessages,
    loading,
    error,
    refetch: refetchItinerary,
  } = useItineraryData(id);

  // Prefer new engine (segmentsV2) when present so itinerary uses V2 layout in QA/production
  const hasSegmentsV2 = segmentsV2?.days?.length > 0;

  // ============================================================================
  // SECURITY: Only log non-PII debug info in development
  // Never log full itinerary objects (may contain client names, emails, etc.)
  // ============================================================================
  // Use ref to prevent double logging in React StrictMode
  const hasLoggedRef = useRef(false);
  if (process.env.NODE_ENV !== 'production' && !hasLoggedRef.current && itinerary) {
    console.log('🏠 UnifiedItineraryView - Itinerary loaded:', {
      tripId: itinerary?.TripId,
      hasDays: !!itinerary?.DailyItineraries,
      dayCount: itinerary?.DailyItineraries?.length,
      hasQuestionnaireData: !!itinerary?.questionnaireData
    });
    hasLoggedRef.current = true;
  }
  
  const hasLoggedDaysRef = useRef(false);
  if (!hasLoggedDaysRef.current && days && days.length > 0) {
    console.log('📅 UnifiedItineraryView - Days data:', days);
    console.log('📋 UnifiedItineraryView - First day structure:', days[0]);
    hasLoggedDaysRef.current = true;
  }

  // Extract guest information using utility function
  const guestInfo = useMemo(() => {
    if (!itinerary || !tripDetails) return null;
    return extractGuestInfo(itinerary, tripDetails);
  }, [itinerary, tripDetails]);

  // Normalize itinerary data if needed (safety check for raw data)
  // Use normalizedItinerary as a fallback source for questionnaireData and tripSummary
  const normalizedItinerary = useMemo(() => {
    if (!itinerary) return null;
    // Use normalizeItineraryData as a safety measure for data structure consistency
    // This ensures we have normalized questionnaireData and tripSummary even if hook doesn't provide them
    return normalizeItineraryData(itinerary);
  }, [itinerary]);
  
  // Use normalized questionnaireData as fallback if itinerary.questionnaireData is not available
  const normalizedQuestionnaireData = useMemo(() => {
    return normalizedItinerary?.questionnaireData || itinerary?.questionnaireData;
  }, [normalizedItinerary, itinerary]);

  // Parsed questionnaire data for Client Snapshot (object only; no runtime error if missing)
  const questionnaireDataForSnapshot = useMemo(() => {
    const raw = normalizedQuestionnaireData ?? itinerary?.questionnaireData;
    if (raw == null) return {};
    return typeof raw === "string" ? safeJsonParse(raw) || {} : raw;
  }, [normalizedQuestionnaireData, itinerary?.questionnaireData]);

  // Transform itinerary data if needed for consistent structure
  // Use transformedDays if days need structural normalization, otherwise use days directly
  const transformedDays = useMemo(() => {
    if (!days || !Array.isArray(days) || days.length === 0) return days;
    // Use transformItineraryData to ensure consistent day data structure
    // This helps with edge cases where day structures might vary
    try {
      return transformItineraryData(days);
    } catch (error) {
      console.warn('Error transforming itinerary data, using original:', error);
      return days;
    }
  }, [days]);
  
  // When segmentsV2 exists: merge full day list with V2 so arrival/departure days still show.
  // Backend segmentsV2.days only includes theme-park days (with segments); arrival/departure have no segments and are omitted.
  // Full list (transformedDays/days) has all days; for each index i use V2 day if dayIndex === i, else legacy day.
  const daysToFilter = useMemo(() => {
    const fullList = transformedDays?.length > 0 ? transformedDays : (Array.isArray(days) ? days : []);
    if (hasSegmentsV2 && segmentsV2?.days?.length && fullList.length > 0) {
      const v2ByDayIndex = new Map((segmentsV2.days || []).map((d) => [d.dayIndex, d]));
      return fullList.map((day, i) => {
        const v2 = v2ByDayIndex.get(i);
        if (v2) return { ...v2, date: v2.date ?? v2.dateISO };
        return { ...day, date: day.date ?? day.dateISO };
      });
    }
    if (hasSegmentsV2 && segmentsV2?.days?.length) {
      return segmentsV2.days.map((d) => ({ ...d, date: d.date ?? d.dateISO }));
    }
    return fullList;
  }, [hasSegmentsV2, segmentsV2?.days, transformedDays, days]);

  // Get all specialty experiences across entire itinerary using utility function
  const allSpecialtyExperiences = useMemo(() => {
    if (!itinerary) return [];
    return getAllSpecialtyExperiences(itinerary);
  }, [itinerary]);

  // Helper functions for Trip Summary (canonical date parsing)
  const getTripStatus = (startDate, endDate) => {
    const start = parseDateForCompare(startDate);
    const end = parseDateForCompare(endDate);
    if (start == null || end == null) return "Planning";

    const today = parseDateForCompare(new Date());
    if (today == null) return "Planning";

    if (today < start) return "Planning";
    if (today >= start && today <= end) return "In Progress";
    return "Completed";
  };

  const getTotalGuests = () => {
    // Use normalized questionnaire data with safeJsonParse fallback
    const questionnaireData = normalizedQuestionnaireData
      ? (typeof normalizedQuestionnaireData === 'string' 
          ? safeJsonParse(normalizedQuestionnaireData) 
          : normalizedQuestionnaireData)
      : (itinerary?.questionnaireData 
          ? (typeof itinerary.questionnaireData === 'string' 
              ? safeJsonParse(itinerary.questionnaireData) 
              : itinerary.questionnaireData)
          : {});
    
    const groupInfo = questionnaireData?.groupInfo || {};
    const adults = parseInt(groupInfo.adults || "0", 10);
    const kids = parseInt(groupInfo.kids || "0", 10);
    return adults + kids;
  };

  const getAdultCount = () => {
    const questionnaireData = normalizedQuestionnaireData
      ? (typeof normalizedQuestionnaireData === 'string' 
          ? safeJsonParse(normalizedQuestionnaireData) 
          : normalizedQuestionnaireData)
      : (itinerary?.questionnaireData 
          ? (typeof itinerary.questionnaireData === 'string' 
              ? safeJsonParse(itinerary.questionnaireData) 
              : itinerary.questionnaireData)
          : {});
    return questionnaireData?.groupInfo?.adults || "0";
  };

  const getKidCount = () => {
    const questionnaireData = normalizedQuestionnaireData
      ? (typeof normalizedQuestionnaireData === 'string' 
          ? safeJsonParse(normalizedQuestionnaireData) 
          : normalizedQuestionnaireData)
      : (itinerary?.questionnaireData 
          ? (typeof itinerary.questionnaireData === 'string' 
              ? safeJsonParse(itinerary.questionnaireData) 
              : itinerary.questionnaireData)
          : {});
    return parseInt(questionnaireData?.groupInfo?.kids || "0", 10);
  };

  const getAdultMalesCount = () => {
    const questionnaireData = normalizedQuestionnaireData
      ? (typeof normalizedQuestionnaireData === 'string' 
          ? safeJsonParse(normalizedQuestionnaireData) 
          : normalizedQuestionnaireData)
      : (itinerary?.questionnaireData 
          ? (typeof itinerary.questionnaireData === 'string' 
              ? safeJsonParse(itinerary.questionnaireData) 
              : itinerary.questionnaireData)
          : {});
    return questionnaireData?.groupInfo?.numAdultMales || "0";
  };

  const getAdultFemalesCount = () => {
    const questionnaireData = normalizedQuestionnaireData
      ? (typeof normalizedQuestionnaireData === 'string' 
          ? safeJsonParse(normalizedQuestionnaireData) 
          : normalizedQuestionnaireData)
      : (itinerary?.questionnaireData 
          ? (typeof itinerary.questionnaireData === 'string' 
              ? safeJsonParse(itinerary.questionnaireData) 
              : itinerary.questionnaireData)
          : {});
    return questionnaireData?.groupInfo?.numAdultFemales || "0";
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Planning":
        return "text-blue-600";
      case "In Progress":
        return "text-green-600";
      case "Completed":
        return "text-gray-600";
      default:
        return "text-gray-600";
    }
  };

  // UI state
  const [regenerating, setRegenerating] = useState(false);
  // PDF export disabled for Phase 1
  // const [downloadingPDF, setDownloadingPDF] = useState(false);
  
  // Regeneration modal state (Phase 2: only "Regenerate entire itinerary" — selected-days removed)
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  
  // Edit functionality state
  const [editingDay, setEditingDay] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [, setSavingChanges] = useState(false); // State setter used, value never read



  const toggleParkFilter = (p) =>
    setFilters((f) => {
      const exists = f.parks.includes(p);
      const parks = exists ? f.parks.filter((x) => x !== p) : [...f.parks, p];
      return { ...f, parks };
    });

  // Edit functionality handlers
  const handleEditDay = useCallback((dayData) => {
    setEditingDay(dayData);
    setIsEditModalOpen(true);
  }, []);

  const handleSaveChanges = useCallback(async (dayIndex, updatedDay) => {
    setSavingChanges(true);
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/api/itinerary/${id}/update`, 
        {
          dayIndex,
          updatedDay,
          originalDay: editingDay,
          changeType: 'manual_edit',
          changeDescription: `Agent manually edited day ${dayIndex + 1} using drag & drop interface`
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success(`Day ${dayIndex + 1} updated successfully!`);
        
        // Refresh the itinerary data
        window.location.reload(); // Simple refresh for now, could be optimized
        
        setIsEditModalOpen(false);
        setEditingDay(null);
      } else {
        throw new Error(response.data.message || 'Failed to save changes');
      }
    } catch (error) {
      console.error('Error saving changes:', error);
      toast.error(`Failed to save changes: ${error.response?.data?.error || error.message}`);
    } finally {
      setSavingChanges(false);
    }
  }, [id, editingDay]);

  const handleCancelEdit = useCallback(() => {
    setIsEditModalOpen(false);
    setEditingDay(null);
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setIsEditModalOpen(false);
    setEditingDay(null);
  }, []);

  const toggleDayType = (t) =>
    setFilters((f) => {
      const exists = f.dayTypes.includes(t);
      const dayTypes = exists
        ? f.dayTypes.filter((x) => x !== t)
        : [...f.dayTypes, t];
      return { ...f, dayTypes };
    });

  const toggleActivityType = (activityType) =>
    setFilters((f) => {
      const exists = f.activityTypes.includes(activityType);
      const activityTypes = exists
        ? f.activityTypes.filter((x) => x !== activityType)
        : [...f.activityTypes, activityType];
      return { ...f, activityTypes };
    });

  const toggleFlag = (k) =>
    setFilters((f) => ({ ...f, flags: { ...f.flags, [k]: !f.flags[k] } }));

  const setDateRange = (start, end) =>
    setFilters((f) => ({ ...f, dateRange: { start, end } }));

  const clearFilters = () => {
    setFilters({
      parks: [],
      dayTypes: [],
      flags: { dining: false, nighttime: false },
      dateRange: { start: null, end: null },
      activityTypes: [],
    });
  };


  const toggleDropdown = (dropdown) => {
    setOpenDropdowns((prev) => {
      // If the clicked dropdown is already open, close it
      if (prev[dropdown]) {
        return { ...prev, [dropdown]: false };
      }
      // Otherwise, close all dropdowns and open only the clicked one
      const newState = Object.keys(prev).reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {});
      newState[dropdown] = true;
      return newState;
    });
  };

  const closeAllDropdowns = () => {
    setOpenDropdowns({
      parks: false,
      dayTypes: false,
      features: false,
      jumpTo: false,
      activityTypes: false,
      regenerate: false,
    });
  };

  // Enhanced modal state with editing capabilities
  const [showModal, setShowModal] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [showAgentNotesModal, setShowAgentNotesModal] = useState(false);
  const [showRidesModal, setShowRidesModal] = useState(false);
  const [showRestaurantsModal, setShowRestaurantsModal] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [editContent, setEditContent] = useState("");
  const [, setSavingEdit] = useState(false); // State setter used, value never read
  const modalRef = useRef(null);
  const agentNotesModalRef = useRef(null);
  const [openDropdowns, setOpenDropdowns] = useState({
    parks: false,
    dayTypes: false,
    features: false,
    jumpTo: false,
    activityTypes: false,
    dateRange: false,
    regenerate: false,
    pdfDownload: false,
  });
  const lastFocusRef = useRef(null);


  useEffect(() => {
    if (showModal) {
      lastFocusRef.current = document.activeElement;
      const el = modalRef.current;
      el?.focus();
      const onKey = (e) => {
        if (e.key === "Escape") handleCloseModal();
        if (e.key === "Tab") {
          const focusables = el.querySelectorAll(
            'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
          );
          const list = Array.from(focusables);
          if (list.length === 0) return;
          const first = list[0];
          const last = list[list.length - 1];
          if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      };
      el?.addEventListener("keydown", onKey);
      return () => el?.removeEventListener("keydown", onKey);
    } else {
      lastFocusRef.current?.focus?.();
    }
  }, [showModal]);

  // Agent Notes modal focus management
  useEffect(() => {
    if (showAgentNotesModal) {
      lastFocusRef.current = document.activeElement;
      const el = agentNotesModalRef.current;
      el?.focus();
      const onKey = (e) => {
        if (e.key === "Escape") handleCloseAgentNotesModal();
        if (e.key === "Tab") {
          const focusables = el.querySelectorAll(
            'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
          );
          const list = Array.from(focusables);
          if (list.length === 0) return;
          const first = list[0];
          const last = list[list.length - 1];
          if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      };
      el?.addEventListener("keydown", onKey);
      return () => el?.removeEventListener("keydown", onKey);
    } else {
      lastFocusRef.current?.focus?.();
    }
  }, [showAgentNotesModal]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        closeAllDropdowns();
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Enhanced filtering with search
  const [filters, setFilters] = useState({
    parks: [], // strings
    dayTypes: [], // Arrival | Departure | Rest | Springs | Park
    flags: { dining: false, nighttime: false },
    dateRange: { start: null, end: null },
      activityTypes: [], // rides, shows, dining, experiences
  });

  // Enhanced filtering with search functionality
  // Use daysToFilter (which may be transformed) for consistent filtering
  const filteredDays = useMemo(() => {
    if (!daysToFilter?.length) return [];
    
    const filtered = daysToFilter.filter((info) => {
      const t = analyzeDayType(info);


      // day type filters
      if (filters.dayTypes.length) {
        const dayLabels = [];
        if (t.isArrival) dayLabels.push("Arrival");
        if (t.isDeparture) dayLabels.push("Departure");
        if (t.isRest) dayLabels.push("Rest");
        if (t.isSprings) dayLabels.push("Springs");
        if (!t.isArrival && !t.isDeparture && !t.isRest && !t.isSprings)
          dayLabels.push("Park");
        if (!filters.dayTypes.some((x) => dayLabels.includes(x))) return false;
      }

      // park filters - check if any selected park matches any park in the day
      if (filters.parks.length) {
        const dayParks = getDayParks(info);
        const hasMatchingPark = filters.parks.some((selectedPark) => 
          dayParks.some((dayPark) => dayPark.includes(selectedPark) || selectedPark.includes(dayPark))
        );
        if (!hasMatchingPark) return false;
      }

      // activity type filters
      if (filters.activityTypes.length) {
        const hasMatchingActivity = filters.activityTypes.some((activityType) => {
          switch (activityType) {
            case 'rides':
              return getAllRides(info).length > 0;
            case 'shows':
              return getAllShows(info).length > 0;
            case 'shopping':
              return getAllRetail(info).length > 0;
            case 'dining':
              return hasDiningInfo(info);
            case 'experiences':
              return hasSpecialtyExperiences(info);
            default:
              return false;
          }
        });
        if (!hasMatchingActivity) return false;
      }

      // date range filters: include undated days (do not silently hide them)
      if (filters.dateRange.start || filters.dateRange.end) {
        const dayDate = parseDateForCompare(info?.date ?? info?.dateISO);
        if (dayDate == null) return true; // Include days with no valid date
        const rangeStart = parseDateForCompare(filters.dateRange.start);
        const rangeEnd = parseDateForCompare(filters.dateRange.end);
        if (rangeStart != null && dayDate < rangeStart) return false;
        if (rangeEnd != null && dayDate > rangeEnd) return false;
      }

      // flags
      if (filters.flags.dining && !hasDining(info)) return false;
      if (filters.flags.nighttime && !hasNighttime(info)) return false;

      return true;
    });
    
    return filtered;
  }, [daysToFilter, filters]);

  // Actions
  // PDF export disabled for Phase 1
  // const handlePDFDownload = async () => { ... };
  // const handleNewPDFDownload = async () => { ... };

  // Open regenerate confirmation (entire itinerary only)
  const openRegenerateModal = () => setShowRegenerateModal(true);

  const closeRegenerateModals = () => setShowRegenerateModal(false);

  const handleRegenerate = async () => {
    try {
      setRegenerating(true);
      const tripId = itineraryId ?? id;
      const endpoint = `${import.meta.env.VITE_API_URL}/api/itineraries/${tripId}/regenerate`;
      toast.info("Regenerating entire itinerary... This may take a moment.");
      const response = await axios.post(endpoint, {}, { withCredentials: true });
      const ok = response?.status >= 200 && response?.status < 300;
      const successStatus = response?.data?.status === "success";
      if (ok && (successStatus || response?.data?.data?.success)) {
        setShowRegenerateModal(false);
        // Apply POST response then refetch; reload so UI always shows fresh DB state
        await refetchItinerary({ preloaded: response?.data });
        toast.success("Entire itinerary regenerated successfully!");
        setTimeout(() => window.location.reload(), 1200);
      } else {
        throw new Error(response?.data?.message || "Invalid response from server");
      }
    } catch (error) {
      console.error("Regenerate error:", error);
      if (error.response?.status === 404) {
        toast.error("Itinerary not found or access denied");
      } else if (error.response?.status === 403) {
        toast.error("You don't have permission to regenerate this itinerary");
      } else if (error.response?.data?.message) {
        toast.error(`Failed to regenerate: ${error.response.data.message}`);
      } else {
        toast.error("Failed to regenerate itinerary. Please try again.");
      }
    } finally {
      setRegenerating(false);
      setShowRegenerateModal(false);
    }
  };

  const handleGenerateClientLink = async () => {
    try {
      const tripId = itineraryId ?? id;
      let response;
      
      // Try to get existing public token first
      try {
        response = await axios.get(
          `${import.meta.env.VITE_API_URL}/api/itineraries/${tripId}/public-token`,
          { withCredentials: true }
        );
      } catch (getError) {
        // If GET returns 404 (token doesn't exist), generate a new one with POST
        if (getError.response?.status === 404) {
          response = await axios.post(
            `${import.meta.env.VITE_API_URL}/api/itineraries/${tripId}/public-token`,
            {},
            { withCredentials: true }
          );
        } else {
          throw getError;
        }
      }
      
      if (response.data && response.data.publicToken) {
        const clientBase = import.meta.env.VITE_CLIENT_APP_URL;
        if (!clientBase) {
          console.warn('VITE_CLIENT_APP_URL is not set, sharing links may not work correctly');
        }
        const clientUrl = `${clientBase}/i/${response.data.publicToken}`;
        
        // Try to copy to clipboard
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(clientUrl);
          toast.success("Client link copied to clipboard!");
        } else {
          // Fallback for older browsers or non-secure contexts
          const textArea = document.createElement('textarea');
          textArea.value = clientUrl;
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          try {
            document.execCommand('copy');
            toast.success("Client link copied to clipboard!");
          } catch {
            // If copy fails, show the URL in a modal or alert
            prompt("Copy this client link:", clientUrl);
            toast.info("Client link displayed - please copy it manually");
          }
          document.body.removeChild(textArea);
        }
      } else {
        toast.error("Failed to generate client link - invalid response");
      }
    } catch (error) {
      console.error("Share error:", error);
      if (error.response?.status === 404) {
        toast.error("Itinerary not found or access denied");
      } else if (error.response?.status === 403) {
        toast.error("You don't have permission to share this itinerary");
      } else {
        toast.error("Failed to generate client link. Please try again.");
      }
    }
  };

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const saveTripName = async () => {
    if (!tripName.trim()) return;
    try {
      setSaving(true);
      const tripId = itineraryId ?? id;
      await axios.patch(
        `${import.meta.env.VITE_API_URL}/api/itineraries/${tripId}/rename`,
        { tripName },
        { withCredentials: true }
      );
      setEditing(false);
      toast.success("Trip name updated!");
      
      // Emit real-time update event for clients
      await emitTripRename(tripId, tripName);
    } catch (e) {
      console.error(e);
      toast.error("Failed to save trip name");
    } finally {
      setSaving(false);
    }
  };

  // Jump to date
  const handleJump = (idx) => {
    const el = document.getElementById(`day-${idx}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Enhanced editing functionality
  const _startEditing = (section, content) => {
    setEditingSection(section);
    setEditContent(Array.isArray(content) ? content.join('\n') : content || '');
  };

  const cancelEditing = () => {
    setEditingSection(null);
    setEditContent("");
  };

  const _saveEdit = async () => {
    if (!selectedDay || !editingSection) return;
    
    try {
      setSavingEdit(true);
      const tripId = itineraryId ?? id;
      
      // Convert content back to appropriate format
      let processedContent = editContent;
      if (editingSection === 'morning' || editingSection === 'afternoon') {
        processedContent = editContent.split('\n').filter(line => line.trim());
      }
      
      await axios.patch(
        `${import.meta.env.VITE_API_URL}/api/itineraries/${tripId}/days/${selectedDay.index}/sections/${editingSection}`,
        { 
          content: processedContent,
          editNotes: `Updated ${editingSection} section`
        },
        { withCredentials: true }
      );
      
      // Update local state optimistically
      // NOTE: days comes from useItineraryData hook which doesn't provide setDays
      // The data will be refreshed on next hook refetch or page reload
      // const updatedDays = [...days];
      // updatedDays[selectedDay.index][editingSection] = processedContent;
      // setDays(updatedDays);
      
      // Update selected day
      setSelectedDay({
        ...selectedDay,
        [editingSection]: processedContent
      });
      
      toast.success(`${editingSection} section updated successfully`);
      
      // Emit real-time update event for clients
      await emitItineraryUpdate(tripId, {
        dayIndex: selectedDay.index,
        section: editingSection,
        content: processedContent,
        editNotes: `Updated ${editingSection} section`
      });
      
      cancelEditing();
      
    } catch (error) {
      console.error('Error saving edit:', error);
      toast.error('Failed to save changes');
    } finally {
      setSavingEdit(false);
    }
  };

  const _copyToClipboard = useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy to clipboard');
    }
  }, []);

  // Memoized event handlers for performance
  const handleViewDetails = useCallback((dayWithIndex) => {
    console.log('🔍 Opening View Details modal, current states:', {
      showModal: false,
      showAgentNotesModal: showAgentNotesModal
    });
    setSelectedDay(dayWithIndex);
    setShowModal(true);
  }, [showAgentNotesModal]);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setEditingSection(null);
    setEditContent("");
  }, []);

  const handleCloseAgentNotesModal = useCallback(() => {
    setShowAgentNotesModal(false);
  }, []);

  // Enhanced loading state with skeleton
  if (loading) {
    return (
      <div className="flex flex-col min-h-screen bg-gray-50">
        {/* Header Skeleton */}
        <div className="sticky top-0 z-40 border-b backdrop-blur bg-white/80">
          <div className="px-6 py-3 mx-auto w-full max-w-7xl">
            <div className="flex gap-4 justify-between items-center">
              <div className="flex gap-3 items-center">
                <div className="w-20 h-10 bg-gray-200 rounded-lg animate-pulse"></div>
                <div className="w-48 h-6 bg-gray-200 rounded animate-pulse"></div>
          </div>
              <div className="flex gap-3 items-center">
                <div className="w-24 h-10 bg-gray-200 rounded-lg animate-pulse"></div>
                <div className="w-32 h-10 bg-gray-200 rounded-lg animate-pulse"></div>
                <div className="w-20 h-10 bg-gray-200 rounded-lg animate-pulse"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters Skeleton */}
        <div className="px-6 py-4 mx-auto w-full max-w-7xl">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="p-4">
              <div className="flex justify-between items-center mb-4">
                <div className="w-32 h-6 bg-gray-200 rounded animate-pulse"></div>
                <div className="flex gap-3 items-center">
                  <div className="w-16 h-4 bg-gray-200 rounded animate-pulse"></div>
                  <div className="w-20 h-8 bg-gray-200 rounded-lg animate-pulse"></div>
                </div>
              </div>
              <div className="mb-4 w-full h-10 bg-gray-200 rounded-lg animate-pulse"></div>
              <div className="flex gap-4 items-center">
                <div className="w-24 h-8 bg-gray-200 rounded-lg animate-pulse"></div>
                <div className="w-28 h-8 bg-gray-200 rounded-lg animate-pulse"></div>
                <div className="w-20 h-8 bg-gray-200 rounded-lg animate-pulse"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Skeleton */}
        <div className="flex-1 w-full">
          <div className="px-6 pb-10 mx-auto w-full max-w-7xl">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="overflow-hidden bg-white rounded-xl border border-gray-200 shadow-sm">
                  <div className="p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <div className="mb-2 w-32 h-5 bg-gray-200 rounded animate-pulse"></div>
                        <div className="w-24 h-4 bg-gray-200 rounded animate-pulse"></div>
                      </div>
                      <div className="w-16 h-6 bg-gray-200 rounded-full animate-pulse"></div>
                    </div>
                    <div className="flex gap-2 mb-4">
                      <div className="w-16 h-6 bg-gray-200 rounded-full animate-pulse"></div>
                      <div className="w-20 h-6 bg-gray-200 rounded-full animate-pulse"></div>
                    </div>
                    <div className="w-full h-10 bg-gray-200 rounded-lg animate-pulse"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Enhanced error state with retry options
  // Also check if we have no days and no segmentsV2 and no loading (indicating a failed fetch)
  if (error || (!loading && !error && days.length === 0 && !hasSegmentsV2 && id)) {
    // If it's a 404 or we have no data after loading, show error
    const errorMessage = error || "Unable to load itinerary. The itinerary may not exist or you may not have access to it.";
    const isNotFound = errorMessage.toLowerCase().includes('not found') || errorMessage.toLowerCase().includes('not exist');
    
    return (
      <div className="flex w-full min-h-screen justify-center items-center bg-gray-50">
        <div className="p-8 w-full max-w-md text-center flex flex-col items-center">
          <div className="flex justify-center items-center mx-auto mb-4 w-16 h-16 bg-red-100 rounded-full">
            <span className="text-2xl">⚠️</span>
          </div>
          <h2 className="mb-2 text-xl font-semibold text-gray-900">Unable to load itinerary</h2>
          <p className="mb-6 text-gray-600">{errorMessage}</p>
          <div className="space-y-3">
          <button
              onClick={() => window.location.reload()}
            className="inline-flex gap-2 items-center px-6 py-3 font-medium text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700"
            >
              <RefreshCw className="w-4 h-4" />
              Try Again
            </button>
            {isNotFound && (
              <button
                onClick={() => navigate('/agent/crm/trips')}
                className="inline-flex gap-2 items-center px-6 py-3 font-medium text-white bg-gray-600 rounded-lg transition-colors hover:bg-gray-700"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Trips
              </button>
            )}
            <button
              onClick={() => {
                const landingPath = user ? getLandingPathForRole(user.role) : '/agent/dashboard';
                navigate(landingPath);
              }}
              className="inline-flex gap-2 items-center px-6 py-3 font-medium text-gray-700 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50"
          >
            <ArrowLeft className="w-4 h-4" />
              Go to Dashboard
          </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ItineraryErrorBoundary
      onGoBack={() => navigate(-1)}
    >
      <style>{PRINT_STYLES}</style>
      <div 
        className={`flex flex-col min-h-0 flex-1 overflow-y-auto w-full bg-gray-50 style-variant-${styleVariant} transition-opacity duration-200 ease-out ${
          isMounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-[2px]'
        }`}
      >
        {/* Skip Link for Accessibility */}
        <a 
          href="#main-content" 
          className="z-50 px-4 py-2 text-white bg-blue-600 rounded-lg sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          Skip to main content
        </a>
        {/* Header (sticky) */}
        <div
          id="appHeader"
          className="sticky top-0 z-40 border-b backdrop-blur bg-white/80"
        >
          <div className="px-6 py-3 mx-auto w-full max-w-7xl">
            <div className="flex gap-4 justify-between items-center">
              <div className="flex gap-3 items-center">
                <button
                  onClick={() => navigate("/agent/crm/trips")}
                  className="inline-flex gap-2 items-center px-3 py-2 rounded-lg border hover:bg-gray-50"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </button>

                <div className="flex gap-3 items-center">
                  {!editing ? (
                    <>
                      <h1 className="text-lg font-semibold text-gray-900">
                        {decodeHtmlEntities(tripName) || "Trip"}
                      </h1>
                      <button
                        onClick={() => setEditing(true)}
                        className="p-1 text-gray-500 hover:text-gray-700"
                        aria-label="Edit trip name"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                    </>
                  ) : (
                    <div className="flex gap-2 items-center">
                      <input
                        value={decodeHtmlEntities(tripName)}
                        onChange={(e) => setTripName(e.target.value)}
                        className="px-2 py-1 rounded-md border"
                      />
                      <button
                        onClick={saveTripName}
                        disabled={saving}
                        className="px-3 py-1 text-white bg-blue-600 rounded-md disabled:opacity-50"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditing(false)}
                        className="px-3 py-1 rounded-md border"
                      >
                        Cancel
                      </button>
                    </div>
                  )}

                </div>
              </div>

              {/* Action cluster - Improved styling and grouping */}
              <div id="actionsRail" className="flex gap-3 items-center">
                {/* Layout Switcher (dev only) */}
                {import.meta.env.DEV && (
                  <div className="flex gap-2 items-center px-2 py-1 text-xs font-medium text-gray-600 bg-gray-50 rounded border border-gray-300">
                    <span>Layout:</span>
                    <select
                      value={layout}
                      onChange={(e) => {
                        const newLayout = e.target.value;
                        setLayout(newLayout);
                        localStorage.setItem('itineraryLayout', newLayout);
                        // Update URL without page reload
                        const url = new URL(window.location);
                        url.searchParams.set('layout', newLayout);
                        window.history.pushState({}, '', url);
                      }}
                      className="px-2 py-1 text-xs font-medium text-gray-700 bg-white rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value={LAYOUTS.ACCORDION}>Accordion</option>
                      <option value={LAYOUTS.LEGACY}>Day Cards</option>
                    </select>
                  </div>
                )}
                
                {/* Primary Actions */}
                <div className="flex gap-2 items-center">
                  <button
                    onClick={() => {
                      console.log('📝 Opening Agent Notes modal');
                      setShowAgentNotesModal(true);
                    }}
                    className="inline-flex gap-2 items-center px-4 py-2 font-medium text-gray-700 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50"
                  >
                    <Star className="w-4 h-4" />
                    <span className="hidden sm:inline">Agent Notes</span>
                  </button>

                  {/* PDF export disabled for Phase 1 */}


                  <button
                    onClick={handleGenerateClientLink}
                    className="inline-flex gap-2 items-center px-4 py-2 font-medium text-gray-700 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50"
                  >
                    <Share2 className="w-4 h-4" />
                    <span className="hidden sm:inline">Share</span>
                  </button>

                  {/* Regenerate entire itinerary only (Phase 2: selected-days removed) */}
                  <button
                    disabled={regenerating}
                    onClick={openRegenerateModal}
                    className="inline-flex gap-2 items-center px-4 py-2 font-medium text-orange-700 bg-orange-50 rounded-lg border border-orange-200 transition-colors hover:bg-orange-100 disabled:opacity-50"
                  >
                    <RefreshCw
                      className={`w-4 h-4 ${regenerating ? "animate-spin" : ""}`}
                    />
                    <span className="hidden sm:inline">{regenerating ? "Regenerating…" : "Regenerate"}</span>
                    <span className="sm:hidden">{regenerating ? "..." : "↻"}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Client Snapshot (collapsed by default) */}
        <ClientSnapshot
          questionnaireData={questionnaireDataForSnapshot}
          defaultCollapsed={true}
        />

         {/* Enhanced Search and Filters */}
         <div id="filtersRow" className="px-6 py-3 mx-auto w-full max-w-7xl">
           <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
             <div className="px-4 py-3">
               {/* Header with results count and clear button */}
               <div className="flex justify-between items-center mb-3">
                 <div className="flex gap-3 items-center">
                   <Filter className="w-4 h-4 text-gray-400" />
                   <h2 className="text-sm font-semibold text-gray-900">Filters</h2>
                   <span className="px-2 py-0.5 text-xs font-medium text-gray-600 bg-gray-100 rounded">
                     {filteredDays.length} of {daysToFilter.length}
                   </span>
                 </div>
                 {(filters.parks.length > 0 || filters.dayTypes.length > 0 || filters.activityTypes.length > 0 || 
                   filters.flags.dining || filters.flags.nighttime || filters.dateRange.start || filters.dateRange.end) && (
                   <button
                     onClick={clearFilters}
                     className="px-2.5 py-1 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded transition-colors"
                   >
                     Clear All
                   </button>
                 )}
               </div>

               {/* Filters Row - Compact and Professional */}
               <div className="flex flex-wrap gap-2 items-center">
                 {/* Parks Dropdown */}
                 <div className="relative dropdown-container">
                   <button
                     onClick={() => toggleDropdown('parks')}
                     className={`flex gap-1.5 items-center px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                       filters.parks.length > 0
                         ? 'text-blue-700 bg-blue-50 border-blue-200 hover:bg-blue-100'
                         : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
                     } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                   >
                     <MapPin className="w-3.5 h-3.5" />
                     <span>Parks</span>
                     {filters.parks.length > 0 && (
                       <span className="px-1.5 py-0.5 text-xs font-semibold text-blue-700 bg-blue-100 rounded">
                         {filters.parks.length}
                       </span>
                     )}
                     <ChevronDown className="w-3 h-3" />
                   </button>
                   
                   {openDropdowns.parks && (
                     <div className="absolute left-0 top-full z-10 mt-1 w-64 bg-white rounded-lg border border-gray-200 shadow-lg">
                       <div className="p-2">
                         {[
                           "Magic Kingdom",
                           "EPCOT", 
                           "Hollywood Studios",
                           "Animal Kingdom",
                           "Disney Springs",
                         ].map((park) => (
                           <label
                             key={park}
                             className="flex gap-2 items-center p-2 rounded-md cursor-pointer hover:bg-gray-50"
                           >
                             <input
                               type="checkbox"
                               checked={filters.parks.includes(park)}
                               onChange={() => toggleParkFilter(park)}
                               className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                             />
                             <span className="text-sm text-gray-700">{park}</span>
                           </label>
                         ))}
                       </div>
                     </div>
                   )}
                 </div>

                 {/* Day Types Dropdown */}
                 <div className="relative dropdown-container">
                   <button
                     onClick={() => toggleDropdown('dayTypes')}
                     className={`flex gap-1.5 items-center px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                       filters.dayTypes.length > 0
                         ? 'text-gray-700 bg-gray-50 border-gray-300 hover:bg-gray-100'
                         : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
                     } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                   >
                     <Calendar className="w-3.5 h-3.5" />
                     <span>Day Types</span>
                     {filters.dayTypes.length > 0 && (
                       <span className="px-1.5 py-0.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded">
                         {filters.dayTypes.length}
                       </span>
                     )}
                     <ChevronDown className="w-3 h-3" />
                   </button>
                   
                   {openDropdowns.dayTypes && (
                     <div className="absolute left-0 top-full z-10 mt-1 w-48 bg-white rounded-lg border border-gray-200 shadow-lg">
                       <div className="p-2">
                         {[
                           "Arrival",
                           "Departure", 
                           "Rest",
                           "Springs",
                           "Park",
                         ].map((type) => (
                           <label
                             key={type}
                             className="flex gap-2 items-center p-2 rounded-md cursor-pointer hover:bg-gray-50"
                           >
                             <input
                               type="checkbox"
                               checked={filters.dayTypes.includes(type)}
                               onChange={() => toggleDayType(type)}
                               className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                             />
                             <span className="text-sm text-gray-700">{type}</span>
                           </label>
                         ))}
                       </div>
                     </div>
                   )}
                 </div>

                 {/* Activities Dropdown */}
                 <div className="relative dropdown-container">
                   <button
                     onClick={() => toggleDropdown('activityTypes')}
                     className={`flex gap-1.5 items-center px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                       filters.activityTypes.length > 0
                         ? 'text-green-700 bg-green-50 border-green-200 hover:bg-green-100'
                         : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
                     } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                   >
                     <Ticket className="w-3.5 h-3.5" />
                     <span>Activities</span>
                     {filters.activityTypes.length > 0 && (
                       <span className="px-1.5 py-0.5 text-xs font-semibold text-green-700 bg-green-100 rounded">
                         {filters.activityTypes.length}
                       </span>
                     )}
                     <ChevronDown className="w-3 h-3" />
                   </button>
                   
                   {openDropdowns.activityTypes && (
                     <div className="absolute left-0 top-full z-10 mt-1 w-48 bg-white rounded-lg border border-gray-200 shadow-lg">
                       <div className="p-2">
                         {[
                           { type: 'rides', label: 'Rides & Attractions', icon: Ticket },
                           { type: 'shows', label: 'Shows & Entertainment', icon: Music },
                           { type: 'shopping', label: 'Shopping & Retail', icon: ShoppingBag },
                           { type: 'dining', label: 'Dining', icon: Utensils },
                           { type: 'experiences', label: 'Specialty Experiences', icon: Star },
                         ].map(({ type, label, icon: Icon }) => {
                           const IconComponent = Icon;
                           return (
                             <label
                               key={type}
                               className="flex gap-2 items-center p-2 rounded-md cursor-pointer hover:bg-gray-50"
                             >
                              <input
                                type="checkbox"
                                checked={filters.activityTypes.includes(type)}
                                onChange={() => toggleActivityType(type)}
                                className="w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
                              />
                              <IconComponent className="w-3.5 h-3.5 text-gray-500" />
                              <span className="text-sm text-gray-700">{label}</span>
                             </label>
                           );
                         })}
                       </div>
                     </div>
                   )}
                 </div>

                 {/* Features Dropdown */}
                 <div className="relative dropdown-container">
                   <button
                     onClick={() => toggleDropdown('features')}
                     className={`flex gap-1.5 items-center px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                       (filters.flags.dining || filters.flags.nighttime)
                         ? 'text-purple-700 bg-purple-50 border-purple-200 hover:bg-purple-100'
                         : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
                     } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                   >
                     <Star className="w-3.5 h-3.5" />
                     <span>Features</span>
                     {(filters.flags.dining || filters.flags.nighttime) && (
                       <span className="px-1.5 py-0.5 text-xs font-semibold text-purple-700 bg-purple-100 rounded">
                         {(filters.flags.dining ? 1 : 0) + (filters.flags.nighttime ? 1 : 0)}
                       </span>
                     )}
                     <ChevronDown className="w-3 h-3" />
                   </button>
                   
                   {openDropdowns.features && (
                     <div className="absolute left-0 top-full z-10 mt-1 w-48 bg-white rounded-lg border border-gray-200 shadow-lg">
                       <div className="p-2">
                         <label className="flex gap-2 items-center p-2 rounded-md cursor-pointer hover:bg-gray-50">
                           <input
                             type="checkbox"
                             checked={filters.flags.dining}
                             onChange={() => toggleFlag("dining")}
                             className="w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
                           />
                           <span className="text-sm text-gray-700">Has Dining</span>
                         </label>
                         <label className="flex gap-2 items-center p-2 rounded-md cursor-pointer hover:bg-gray-50">
                           <input
                             type="checkbox"
                             checked={filters.flags.nighttime}
                             onChange={() => toggleFlag("nighttime")}
                             className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
                           />
                           <span className="text-sm text-gray-700">Has Nighttime Show</span>
                         </label>
                       </div>
                     </div>
                   )}
                 </div>

                 {/* Date Range */}
                 <div className="relative dropdown-container">
                   <button
                     onClick={() => toggleDropdown('dateRange')}
                     className={`flex gap-1.5 items-center px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                       (filters.dateRange.start || filters.dateRange.end)
                         ? 'text-orange-700 bg-orange-50 border-orange-200 hover:bg-orange-100'
                         : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
                     } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                   >
                     <Calendar className="w-3.5 h-3.5" />
                     <span>
                       {filters.dateRange.start && filters.dateRange.end
                         ? `${filters.dateRange.start} → ${filters.dateRange.end}`
                         : filters.dateRange.start
                         ? `From ${filters.dateRange.start}`
                         : filters.dateRange.end
                         ? `Until ${filters.dateRange.end}`
                         : 'Date Range'}
                     </span>
                     {(filters.dateRange.start || filters.dateRange.end) && (
                       <span className="px-1.5 py-0.5 text-xs font-semibold text-orange-700 bg-orange-100 rounded">
                         1
                       </span>
                     )}
                     <ChevronDown className="w-3 h-3" />
                   </button>
                   
                   {openDropdowns.dateRange && (
                     <div className="absolute left-0 top-full z-10 mt-1 w-72 bg-white rounded-lg border border-gray-200 shadow-lg">
                       <div className="p-3">
                         <div className="space-y-3">
                           <div>
                             <label className="block mb-1 text-xs font-medium text-gray-700">Start Date</label>
                             <input
                               type="date"
                               value={filters.dateRange.start || ''}
                               onChange={(e) => setDateRange(e.target.value, filters.dateRange.end)}
                               className="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                             />
                           </div>
                           <div>
                             <label className="block mb-1 text-xs font-medium text-gray-700">End Date</label>
                             <input
                               type="date"
                               value={filters.dateRange.end || ''}
                               onChange={(e) => setDateRange(filters.dateRange.start, e.target.value)}
                               className="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                             />
                           </div>
                           {(filters.dateRange.start || filters.dateRange.end) && (
                             <button
                               onClick={() => {
                                 setDateRange(null, null);
                                 setOpenDropdowns(prev => ({ ...prev, dateRange: false }));
                               }}
                               className="w-full px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 rounded-md border border-gray-300 hover:bg-gray-100 transition-colors"
                             >
                               Clear Dates
                             </button>
                           )}
                         </div>
                       </div>
                     </div>
                   )}
                 </div>

                 {/* Jump to Day */}
                 <div className="relative dropdown-container">
                   <button
                     onClick={() => toggleDropdown('jumpTo')}
                     className="flex gap-1.5 items-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                   >
                     <Calendar className="w-3.5 h-3.5" />
                     <span>Jump to Day</span>
                     <ChevronDown className="w-3 h-3" />
                   </button>
                   
                   {openDropdowns.jumpTo && (
                     <div className="absolute left-0 top-full z-10 mt-1 w-64 bg-white rounded-lg border border-gray-200 shadow-lg">
                       <div className="overflow-y-auto p-2 max-h-60">
                         {days.map((d, i) => {
                           const t = analyzeDayType(d);
                           const parks = getDayParks(d);
                           const hopper = parks.length > 1;
                           const arrival = t.isArrival;
                           const departure = t.isDeparture;
                           
                           return (
                             <button
                               key={i}
                               onClick={() => {
                                 handleJump(i);
                                 setOpenDropdowns(prev => ({ ...prev, jumpTo: false }));
                               }}
                               className="p-2 w-full text-left rounded-lg transition-colors hover:bg-gray-50"
                             >
                               <div className="flex justify-between items-center">
                                 <div>
                                   <div className="text-sm font-medium text-gray-900">
                                     {arrival 
                                       ? "Arrival Day" 
                                       : departure 
                                       ? "Departure Day"
                                       : hopper
                                       ? formatHopperTitle(parks)
                                       : parks[0] || `Day ${i + 1}`}
                                   </div>
                                   <div className="flex items-center gap-1.5 text-xs text-gray-500">
                                     {formatDate(d?.date)}
                                     {!hasValidDayDate(d) && (
                                       <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
                                         Date TBD
                                       </span>
                                     )}
                                   </div>
                                 </div>
                                 <div className="flex gap-1">
                                   {arrival && (
                                     <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                       Arrival
                                     </span>
                                   )}
                                   {departure && (
                                     <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                       Departure
                                     </span>
                                   )}
                                   {hopper && !arrival && !departure && (
                                     <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-teal-100 text-teal-800">
                                       Hopper
                                     </span>
                                   )}
                                 </div>
                               </div>
                             </button>
                           );
                         })}
                       </div>
                     </div>
                   )}
                 </div>
               </div>

               {/* Active Filters Summary */}
               {(filters.parks.length > 0 || filters.dayTypes.length > 0 || filters.flags.dining || filters.flags.nighttime || filters.activityTypes.length > 0 || filters.dateRange.start || filters.dateRange.end) && (
                 <div className="pt-4 mt-4 border-t border-gray-200">
                   <div className="flex flex-wrap gap-2 items-center">
                     <span className="text-sm font-medium text-gray-700">Active filters:</span>
                     

                     {/* Date Range */}
                     {(filters.dateRange.start || filters.dateRange.end) && (
                       <span className="inline-flex gap-1 items-center px-2 py-1 text-xs font-medium text-orange-700 bg-orange-100 rounded-full">
                         {filters.dateRange.start && filters.dateRange.end 
                           ? `${filters.dateRange.start} - ${filters.dateRange.end}`
                           : filters.dateRange.start 
                           ? `From ${filters.dateRange.start}`
                           : `Until ${filters.dateRange.end}`
                         }
                         <button
                           onClick={() => setDateRange(null, null)}
                           className="ml-1 text-orange-500 hover:text-orange-700"
                         >
                           <X className="w-3 h-3" />
                         </button>
                       </span>
                     )}

                     {/* Activity Types */}
                     {filters.activityTypes.map((type) => (
                       <span key={type} className="inline-flex gap-1 items-center px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                         {type === 'rides' ? 'Rides' : type === 'shows' ? 'Shows' : type === 'shopping' ? 'Shopping' : type === 'experiences' ? 'Experiences' : 'Dining'}
                         <button
                           onClick={() => toggleActivityType(type)}
                           className="ml-1 text-green-500 hover:text-green-700"
                         >
                           <X className="w-3 h-3" />
                         </button>
                       </span>
                     ))}

                     {/* Parks */}
                     {filters.parks.map((park) => (
                       <span key={park} className="inline-flex gap-1 items-center px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded-full">
                         {park}
                         <button
                           onClick={() => toggleParkFilter(park)}
                           className="ml-1 text-blue-500 hover:text-blue-700"
                         >
                           <X className="w-3 h-3" />
                         </button>
                       </span>
                     ))}

                     {/* Day Types */}
                     {filters.dayTypes.map((type) => (
                       <span key={type} className="inline-flex gap-1 items-center px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded-full">
                         {type}
                         <button
                           onClick={() => toggleDayType(type)}
                           className="ml-1 text-gray-500 hover:text-gray-700"
                         >
                           <X className="w-3 h-3" />
                         </button>
                       </span>
                     ))}

                     {/* Feature Flags */}
                     {filters.flags.dining && (
                       <span className="inline-flex gap-1 items-center px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                         Has Dining
                         <button
                           onClick={() => toggleFlag("dining")}
                           className="ml-1 text-green-500 hover:text-green-700"
                         >
                           <X className="w-3 h-3" />
                         </button>
                       </span>
                     )}
                     {filters.flags.nighttime && (
                       <span className="inline-flex gap-1 items-center px-2 py-1 text-xs font-medium text-purple-700 bg-purple-100 rounded-full">
                         Has Nighttime
                         <button
                           onClick={() => toggleFlag("nighttime")}
                           className="ml-1 text-purple-500 hover:text-purple-700"
                         >
                           <X className="w-3 h-3" />
                         </button>
                       </span>
                     )}
                   </div>
                 </div>
               )}
             </div>
           </div>
         </div>

        {/* Main */}
        <main id="main-content" className="flex-1 w-full" role="main">
          <div className="px-6 pb-10 mx-auto w-full max-w-7xl">
            {/* Day List Overview - day cards or accordion layout (uses segmentsV2 when available) */}
            {filteredDays.length === 0 ? (
              <div className="py-16 text-center">
                <div className="flex justify-center items-center mx-auto mb-4 w-24 h-24 bg-gray-100 rounded-full">
                  <Calendar className="w-12 h-12 text-gray-400" />
                </div>
                <h3 className="mb-2 text-lg font-semibold text-gray-900">No days match your filters</h3>
                <p className="mb-4 text-gray-600">Try adjusting your filter settings to see more days.</p>
                <button
                  onClick={clearFilters}
                  className="inline-flex gap-2 items-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg border border-blue-200 transition-colors hover:bg-blue-100"
                >
                  <X className="w-4 h-4" />
                  Clear All Filters
                </button>
              </div>
            ) : layout === LAYOUTS.ACCORDION ? (
              <ItineraryDaysAccordion
                filteredDays={filteredDays}
                onViewDetails={handleViewDetails}
                onEditDay={handleEditDay}
                magicMessages={magicMessages}
              />
            ) : hasSegmentsV2 ? (
              <Suspense fallback={<div className="py-8 text-center text-gray-500">Loading itinerary...</div>}>
                <ItineraryDaysV2
                  segmentsV2={segmentsV2}
                  magicMessages={magicMessages}
                />
              </Suspense>
            ) : (
              <Suspense fallback={<div className="py-8 text-center text-gray-500">Loading itinerary...</div>}>
                <ItineraryDaysLegacy
                  filteredDays={filteredDays}
                  onViewDetails={handleViewDetails}
                  onEditDay={handleEditDay}
                  magicMessages={magicMessages}
                />
              </Suspense>
            )}
          </div>
        </main>
      </div>

      {/* Modern Day Details Modal */}
      {showModal && selectedDay && (
        <div
          className="flex fixed inset-0 z-[100] justify-center items-center bg-black/40"
          onMouseDown={(e) => {
            // close when clicking overlay
            if (e.target === e.currentTarget) handleCloseModal();
          }}
        >
          <div
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="dayDetailsTitle"
            tabIndex={-1}
            className="bg-white w-full max-w-4xl max-h-[85vh] rounded-2xl shadow-2xl outline-none border-0 animate-fade-in-up flex flex-col overflow-hidden"
          >
            {/* Compact Header */}
            <div className="relative flex-shrink-0 px-6 py-4 bg-gray-50 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <div className="flex-1">
                  <h2
                    id="dayDetailsTitle"
                    className="mb-1 text-lg font-semibold text-gray-900"
                  >
                    Day {selectedDay.index + 1} - {getDayParks(selectedDay).length > 0 
                      ? formatHopperTitle(getDayParks(selectedDay))
                      : selectedDay.dayType === 'arrival' 
                        ? 'Arrival Day'
                        : selectedDay.dayType === 'departure'
                          ? 'Departure Day'
                          : ''
                    }
                  </h2>
                  <div className="flex gap-3 items-center text-xs text-gray-600">
                    <div className="inline-flex gap-1.5 items-center">
                      <Calendar className="w-3 h-3 text-blue-500" />
                      {formatDate(selectedDay?.date)}
                      {selectedDay && !hasValidDayDate(selectedDay) && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
                          Date TBD
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="p-1.5 text-gray-400 rounded-full transition-colors hover:text-gray-600 hover:bg-gray-100"
                  aria-label="Close modal"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Scrollable Content Area */}
            <div className="overflow-y-auto flex-1">
              <div className="p-4 space-y-4">
                {/* Time-Based Schedule — render in CHRONOLOGICAL order (fixes time-travel: ride 1:06 PM before transfer 2 PM) */}
                <div className="space-y-4">
                  {/* Morning Section — chronological order */}
                  {(() => {
                    const morningActivities = getActivitiesByTimeBlock(selectedDay).morning;
                    const morningSorted = ensureMonotonicSchedule(
                      sortActivitiesByTime(morningActivities),
                      `${selectedDay?.date ?? "day"} morning`
                    );
                    const hasMorning = morningSorted.length > 0;
                    return (
                      <div className="bg-yellow-50 rounded-xl border border-yellow-200">
                        <div className="flex gap-3 items-center px-4 py-3 border-b-2 border-yellow-300">
                          <Sun className="w-5 h-5 text-yellow-600" />
                          <h3 className="text-lg font-bold text-gray-900">Morning</h3>
                        </div>
                        <div className="px-4 pt-3 pb-4 space-y-2">
                          {morningSorted.map((a, idx) => <ActivityRow key={`morning-${idx}`} activity={a} blockPrefix="morning" index={idx} />)}
                          {!hasMorning && <p className="px-3 py-2 text-xs italic text-gray-500">Nothing scheduled in this window — flexible time.</p>}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Lunch Section */}
                  {(() => {
                    const diningInfo = getDiningInfo(selectedDay);
                    if (diningInfo.lunch && diningInfo.lunch !== "Lunch not specified") {
                      return (
                        <div className="bg-green-50 rounded-xl border border-green-200">
                          <div className="flex gap-3 items-center px-4 py-3">
                            <Utensils className="w-4 h-4 text-green-600" />
                            <h3 className="text-base font-semibold text-gray-900">Lunch</h3>
                          </div>
                          <div className="px-4 pb-4">
                            <div className="flex gap-2 items-center px-3 py-2 bg-white rounded-lg border border-green-200">
                              <Utensils className="w-3 h-3 text-green-600" />
                              <span className="text-xs font-medium text-gray-900">{diningInfo.lunch}</span>
                              <RestaurantSelectionIndicator 
                                isClientSelected={diningInfo.lunchClientSelected || false}
                                showLabel={false}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}

                  {/* Afternoon Section — chronological order */}
                  {(() => {
                    const afternoonActivities = getActivitiesByTimeBlock(selectedDay).afternoon;
                    const afternoonSorted = ensureMonotonicSchedule(
                      sortActivitiesByTime(afternoonActivities),
                      `${selectedDay?.date ?? "day"} afternoon`
                    );
                    const hasAfternoon = afternoonSorted.length > 0;
                    return (
                      <div className="bg-orange-50 rounded-xl border border-orange-200">
                        <div className="flex gap-3 items-center px-4 py-3 border-b-2 border-orange-300">
                          <Sun className="w-5 h-5 text-orange-600" />
                          <h3 className="text-lg font-bold text-gray-900">Afternoon</h3>
                        </div>
                        <div className="px-4 pt-3 pb-4 space-y-2">
                          {afternoonSorted.map((a, idx) => <ActivityRow key={`afternoon-${idx}`} activity={a} blockPrefix="afternoon" index={idx} />)}
                          {!hasAfternoon && (
                            <p className="px-3 py-2 text-xs italic text-gray-500">Nothing scheduled in this window — flexible time.</p>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Dinner Section */}
                  {(() => {
                    const diningInfo = getDiningInfo(selectedDay);
                    if (diningInfo.dinner && diningInfo.dinner !== "Dinner not specified") {
                      return (
                        <div className="bg-green-50 rounded-xl border border-green-200">
                          <div className="flex gap-3 items-center px-5 py-4">
                            <Utensils className="w-5 h-5 text-green-600" />
                            <h3 className="text-lg font-semibold text-gray-900">Dinner</h3>
                          </div>
                          <div className="px-5 pb-5">
                            <div className="flex gap-2 items-center px-3 py-2 bg-white rounded-lg border border-green-200">
                              <Utensils className="w-3 h-3 text-green-600" />
                              <span className="text-xs font-medium text-gray-900">{diningInfo.dinner}</span>
                              <RestaurantSelectionIndicator 
                                isClientSelected={diningInfo.dinnerClientSelected || false}
                                showLabel={false}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}

                  {/* Evening Section — chronological order */}
                  {(() => {
                    const eveningActivities = getActivitiesByTimeBlock(selectedDay).evening;
                    const eveningSorted = ensureMonotonicSchedule(
                      sortActivitiesByTime(eveningActivities),
                      `${selectedDay?.date ?? "day"} evening`
                    );
                    const hasEvening = eveningSorted.length > 0;
                    return (
                      <div className="bg-purple-50 rounded-xl border border-purple-200">
                        <div className="flex gap-3 items-center px-4 py-3 border-b-2 border-purple-300">
                          <Moon className="w-5 h-5 text-purple-600" />
                          <h3 className="text-lg font-bold text-gray-900">Evening</h3>
                        </div>
                        <div className="px-4 pt-3 pb-4 space-y-2">
                          {eveningSorted.map((a, idx) => <ActivityRow key={`evening-${idx}`} activity={a} blockPrefix="evening" index={idx} />)}
                          {!hasEvening && (
                            <p className="px-3 py-2 text-xs italic text-gray-500">Nothing scheduled in this window — flexible time.</p>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Meal Suggestions (arrival/departure/rest days; non-scheduled) */}
                  {Array.isArray(selectedDay.mealSuggestions) && selectedDay.mealSuggestions.length > 0 && (
                    <MealSuggestionsPanel mealSuggestions={selectedDay.mealSuggestions} />
                  )}

                  {/* Day-level warnings: unmet dining (reasonCode) + unmet characters */}
                  <DayWarningsPanel warnings={selectedDay.warnings} unresolvedDining={selectedDay.__unresolvedDining} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Agent Notes Modal */}
      {showAgentNotesModal && (
        <div
          className="flex fixed inset-0 z-50 justify-center items-center px-4 bg-black/40 sm:px-6"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) handleCloseAgentNotesModal();
          }}
        >
          <div
            ref={agentNotesModalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="agentNotesTitle"
            tabIndex={-1}
            className="bg-white w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-2xl shadow-2xl outline-none border border-gray-200 animate-fade-in-up"
          >
            {/* Header */}
            <div className="flex justify-between items-center px-6 py-4 bg-white border-b border-gray-200">
              <div className="flex gap-2 items-center">
                <div className="flex justify-center items-center w-8 h-8 bg-blue-100 rounded-full">
                  <Star className="w-4 h-4 text-blue-600" />
                </div>
                <h2
                  id="agentNotesTitle"
                  className="text-xl font-bold text-gray-900"
                >
                  Agent Notes
                </h2>
              </div>
              <button
                onClick={handleCloseAgentNotesModal}
                className="p-1.5 text-gray-400 rounded-full transition-colors hover:text-gray-600 hover:bg-gray-100"
                aria-label="Close modal"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
              <div className="p-6 space-y-6">
                {/* Debug: Log itinerary data */}
                
                {/* Client Information Cards */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="flex gap-3 items-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex justify-center items-center w-8 h-8 bg-blue-100 rounded-full">
                      <span className="text-sm">👤</span>
                    </div>
                    <div>
                      <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">Client Name</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {guestInfo?.guestName || tripDetails.guestName || "Guest"}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3 items-center p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex justify-center items-center w-8 h-8 bg-green-100 rounded-full">
                      <MapPin className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">Resort</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {guestInfo?.resort || tripDetails.resort || "Disney Resort"}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3 items-center p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="flex justify-center items-center w-8 h-8 bg-purple-100 rounded-full">
                      <Calendar className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">Trip Dates</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {formatDate(tripDetails.startDate)} – {formatDate(tripDetails.endDate)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Trip Summary */}
                <div className="p-4 bg-white rounded-lg border border-gray-200">
                  <div className="flex gap-2 items-center mb-3">
                    <div className="flex justify-center items-center w-8 h-8 bg-gray-100 rounded-full">
                      <FileText className="w-4 h-4 text-gray-600" />
                    </div>
                    <h3 className="text-base font-semibold text-gray-900">Trip Summary</h3>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="grid grid-cols-2 gap-3">
                      <p><span className="font-medium text-gray-700">Total Days:</span> {daysToFilter.length} days</p>
                      <p><span className="font-medium text-gray-700">Status:</span> <span className={`font-medium ${getStatusColor(getTripStatus(tripDetails.startDate, tripDetails.endDate))}`}>{getTripStatus(tripDetails.startDate, tripDetails.endDate)}</span></p>
                    </div>
                    
                    {/* Group Information */}
                    <div className="pt-2 border-t border-gray-200">
                      <p className="mb-1 text-xs font-medium tracking-wide text-gray-700 uppercase">Group Information</p>
                      <div className="grid grid-cols-2 gap-3">
                        <p><span className="font-medium text-gray-700">Family Size:</span> {getTotalGuests()}</p>
                        <p><span className="font-medium text-gray-700">Adults:</span> {getAdultCount()}</p>
                        {getKidCount() > 0 && (
                          <p><span className="font-medium text-gray-700">Kids:</span> {getKidCount()}</p>
                        )}
                        {(getAdultMalesCount() !== "0" || getAdultFemalesCount() !== "0") && (
                          <>
                            <p><span className="font-medium text-gray-700">Adult Males:</span> {getAdultMalesCount()}</p>
                            <p><span className="font-medium text-gray-700">Adult Females:</span> {getAdultFemalesCount()}</p>
                          </>
                        )}
                        {/* Display all specialty experiences count if available */}
                        {allSpecialtyExperiences.length > 0 && (
                          <p><span className="font-medium text-gray-700">Specialty Experiences:</span> {allSpecialtyExperiences.length} total</p>
                        )}
                      </div>
                    </div>
              </div>
            </div>

                {/* Enhanced Agent Notes */}
                {itinerary && (() => {
                  const agentNotes = processAgentNotes(itinerary);
                  const { sections, summary } = agentNotes;
                  
                  return (
                    <div className="space-y-3">
                      {sections && sections.length > 0 ? sections.map((section, sectionIdx) => (
                        <div key={sectionIdx} className="p-3 bg-white rounded-lg border border-gray-200">
                          <div className="mb-2">
                            <h3 className="text-base font-semibold text-gray-900">{section.title}</h3>
                            {section.subtitle && (
                              <p className="text-xs text-gray-500">{section.subtitle}</p>
                            )}
                          </div>
                          
                          <div className="space-y-2">
                            {section.items.map((item, itemIdx) => (
                              <div key={itemIdx} className="flex justify-between items-start px-3 py-2 bg-gray-50 rounded border border-gray-200">
                                <span className="text-xs font-medium text-gray-700">
                                  {item.label}:
                                </span>
                                <div className="flex gap-2 items-center">
                                  <span className="max-w-xs text-xs text-right text-gray-600">
                                    {item.value}
                                  </span>
                                  {item.hasDetailView && (
                                    <button
                                      onClick={() => {
                                        if (item.detailType === "rides") {
                                          setShowRidesModal(true);
                                        } else if (item.detailType === "restaurants") {
                                          setShowRestaurantsModal(true);
                                        }
                                      }}
                                      className="px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded border border-blue-200 transition-colors hover:bg-blue-100"
                                    >
                                      View Details
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )) : (
                        <div className="p-3 bg-white rounded-lg border border-gray-200">
                          <div className="mb-2">
                            <h3 className="text-base font-semibold text-gray-900">Client Preferences & Notes</h3>
                          </div>
                          <p className="text-xs italic text-gray-500">No additional notes available</p>
                        </div>
                      )}
                      
                      {summary && (
                        <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200">
                          <div className="flex gap-2 items-center mb-1">
                            <span className="text-xs font-semibold tracking-wide text-blue-700 uppercase">Quick Summary</span>
                          </div>
                          <p className="text-xs font-medium text-blue-600">{summary}</p>
                        </div>
                      )}
                    </div>
                  );
                })()}

              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-white border-t border-gray-200">
              <div className="flex justify-end">
                <button
                  onClick={handleCloseAgentNotesModal}
                  className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg shadow-sm transition-colors hover:bg-gray-800"
              >
                Close
              </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rides Detail Modal */}
      {showRidesModal && (
        <div className="flex fixed inset-0 z-50 justify-center items-center px-4 bg-black/40 sm:px-6">
          <div className="w-full max-w-4xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Selected Rides by Park</h2>
              <button
                onClick={() => setShowRidesModal(false)}
                className="p-2 text-gray-400 transition-colors hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              {itinerary && (() => {
                const agentNotes = processAgentNotes(itinerary);
                const ridesData = agentNotes.sections?.find(s => s.title === "Park Priorities")?.items?.find(i => i.detailType === "rides")?.detailData;
                
                if (!ridesData) {
                  return <p className="text-gray-500">No rides data available</p>;
                }

                return (
                  <div className="space-y-4">
                    {Object.entries(ridesData).map(([park, rides]) => {
                      // Convert park abbreviations to full names
                      const parkNames = {
                        'MK': 'Magic Kingdom',
                        'EPCOT': 'EPCOT',
                        'HS': 'Hollywood Studios',
                        'AK': 'Animal Kingdom'
                      };
                      const fullParkName = parkNames[park] || park;
                      
                      return rides.length > 0 && (
                        <div key={park} className="p-4 bg-gray-50 rounded-lg">
                          <h3 className="mb-3 text-lg font-semibold text-gray-900">{fullParkName}</h3>
                          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                            {rides.map((ride, index) => (
                              <div key={index} className="flex items-center p-2 bg-white rounded border border-gray-200">
                                <span className="text-sm text-gray-700">{ride}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
            <div className="px-6 py-4 bg-white border-t border-gray-200">
              <div className="flex justify-end">
                <button
                  onClick={() => setShowRidesModal(false)}
                  className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg shadow-sm transition-colors hover:bg-gray-800"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Restaurants Detail Modal */}
      {showRestaurantsModal && (
        <div className="flex fixed inset-0 z-50 justify-center items-center px-4 bg-black/40 sm:px-6">
          <div className="w-full max-w-4xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Selected Restaurants by Park</h2>
              <button
                onClick={() => setShowRestaurantsModal(false)}
                className="p-2 text-gray-400 transition-colors hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              {itinerary && (() => {
                const agentNotes = processAgentNotes(itinerary);
                const restaurantsData = agentNotes.sections?.find(s => s.title === "Dining Notes")?.items?.find(i => i.detailType === "restaurants")?.detailData;
                
                if (!restaurantsData) {
                  return <p className="text-gray-500">No restaurants data available</p>;
                }

                return (
                  <div className="space-y-4">
                    {Object.entries(restaurantsData).map(([park, restaurants]) => {
                      // Convert park abbreviations to full names
                      const parkNames = {
                        'MK': 'Magic Kingdom',
                        'EPCOT': 'EPCOT',
                        'HS': 'Hollywood Studios',
                        'AK': 'Animal Kingdom'
                      };
                      const fullParkName = parkNames[park] || park;
                      
                      return restaurants.length > 0 && (
                        <div key={park} className="p-4 bg-gray-50 rounded-lg">
                          <h3 className="mb-3 text-lg font-semibold text-gray-900">{fullParkName}</h3>
                          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                            {restaurants.map((restaurant, index) => (
                              <div key={index} className="flex items-center p-2 bg-white rounded border border-gray-200">
                                <span className="text-sm text-gray-700">{restaurant}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
            <div className="px-6 py-4 bg-white border-t border-gray-200">
              <div className="flex justify-end">
                <button
                  onClick={() => setShowRestaurantsModal(false)}
                  className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg shadow-sm transition-colors hover:bg-gray-800"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Drag & Drop Edit Modal */}
      {editingDay && (
        <DragDropActivity
          day={editingDay}
          dayIndex={editingDay.index}
          onSave={handleSaveChanges}
          onCancel={handleCancelEdit}
          isOpen={isEditModalOpen}
          onClose={handleCloseEditModal}
        />
      )}

      {/* Regenerate Confirmation Modal */}
      {showRegenerateModal && (
        <div
          className="flex fixed inset-0 z-50 justify-center items-center px-4 bg-black/40 sm:px-6"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeRegenerateModals();
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="regenerateModalTitle"
            className="w-full max-w-md bg-white rounded-2xl border border-gray-200 shadow-2xl outline-none animate-fade-in-up"
          >
            {/* Header */}
            <div className="flex justify-between items-center px-6 py-4 bg-white border-b border-gray-200">
              <div className="flex gap-3 items-center">
                <div className="flex justify-center items-center w-10 h-10 bg-orange-100 rounded-full">
                  <RefreshCw className="w-5 h-5 text-orange-600" />
                </div>
                <h2
                  id="regenerateModalTitle"
                  className="text-xl font-bold text-gray-900"
                >
                  Confirm Regeneration
                </h2>
              </div>
              <button
                onClick={closeRegenerateModals}
                className="p-2 text-gray-400 rounded-full transition-colors hover:text-gray-600 hover:bg-gray-100"
                aria-label="Close modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content — entire itinerary only (Phase 2: selected-days removed) */}
            <div className="p-6">
              <p className="mb-4 text-gray-700">
                Are you sure you want to regenerate the <strong>entire itinerary</strong>?
                This will completely recreate all days from scratch and may result in different activities, dining, and scheduling.
              </p>
              <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                <p className="text-sm text-orange-800">
                  <strong>Warning:</strong> This action cannot be undone. All current itinerary data will be replaced.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="flex gap-3 justify-end px-6 py-4 bg-gray-50 border-t border-gray-200">
              <button
                onClick={closeRegenerateModals}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRegenerate}
                disabled={regenerating}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg transition-colors hover:bg-orange-700 disabled:opacity-50"
              >
                {regenerating ? (
                  <div className="flex gap-2 items-center">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Regenerating...
                  </div>
                ) : (
                  'Confirm Regeneration'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </ItineraryErrorBoundary>
  );
};

export default UnifiedItineraryView;